import React from "react";
import { View, StyleSheet } from "react-native";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import { useEffect, useState } from "react";
import { PlusCircle } from "../../../components/svgIcons";
import {
  useCreateDegreeMutation,
  useDeleteDegreeMutation,
  CreateDegreeMutation,
  GetDegreesBySchoolByQueryDocument,
  useGetDegreesBySchoolByQueryQuery,
  useUpdateDegreeMutation,
  UpdateDegreeMutation,
} from "../../../graphql/generated/graphql";
import NewCustomAxioActionModal from "../UsersTabContainer/NewCustomAxioActionModal";
import { NewCustomActionButton } from "../../../components/common/general/CustomButton/NewCustomActionButton";
import DegreesTable from "./DegreesTable";
import { useAppConfig } from "../../../AppConfigProvider";
import AddDegreeModal from "./AddDegreeModalContainer";
import DegreeCoursesTabContainer from "../DegreeCoursesTabContainer/DegreeCoursesTabContainer";
import UpdateDegreeModal from "./UpdateDegreeModalContainer";
import DegreeRosterTabContainer from "../DegreeRosterTabContainer/DegreeRosterTabContainer";
import usePagination from "../../../hooks/usePagination";
import TableControlsContainer from "../../general/TableControls/TableControlsContainer";
import CustomSearchInput from "../../../components/common/general/CustomSearchInput/CustomSearchInput";
import SortingDropdown from "../../../components/common/TableControls/SortingDropdown/SortingDropdown";
import useSortingDropdown, { SORT_ITEMS } from "../../../hooks/useSortingDropdown";

const modals = {
  deleteDegree: {
    title: "Delete Degree?",
    ariaLabel: "Delete Degree Modal",
    warningText: "Are you sure you want to permanently delete this degree? This action cannot be undone.",
    actionButtonText: "Delete Degree",
  },
  deleteConfirmation: {
    title: "Degree Deleted",
    ariaLabel: "Degree Deleted Modal",
    warningText: "The degree has been successfully deleted.",
    actionButtonText: "Ok",
  },
};

export enum DegreesTabModalState {
  CLOSED = "CLOSED",
  DELETE_DEGREE = "DELETE_DEGREE",
  DELETE_CONFIRMATION = "DELETE_CONFIRMATION",
  ADD_DEGREE = "ADD_DEGREE",
  DEGREE_COURSES = "DEGREE_COURSES",
  CHANGE_NAME = "CHANGE_NAME",
  DEGREE_ROSTER = "DEGREE_ROSTER",
}

const DegreesTabContainer = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [modalState, setModalState] = useState({
    state: DegreesTabModalState.CLOSED,
    degreeId: "",
    degreeName: "",
  });

  const appConfig = useAppConfig();
  const primaryColor = appConfig.primaryColor;
  const schoolId = appConfig.axioSchools[0];

  const { page, setPage, pageSize, setPageSize } = usePagination({});
  const { sortBy, setSortBy, sortDropdownOpen, setSortDropdownOpen } = useSortingDropdown({
    defaultSortBy: "name_asc",
  });

  const { data: degreesData, refetch: degreesRefetch } = useGetDegreesBySchoolByQueryQuery({
    variables: {
      schoolId,
      query: searchQuery,
      page,
      pageSize,
      sortBy,
    },
  });

  const totalItemsCount = degreesData?.getDegreesBySchoolByQuery?.data.totalCount || 0;

  const refetchQueries = [
    {
      query: GetDegreesBySchoolByQueryDocument,
      variables: {
        schoolId,
        query: searchQuery,
        page,
        pageSize,
        sortBy,
      },
    },
  ];

  const [createDegree] = useCreateDegreeMutation();
  const [deleteDegree] = useDeleteDegreeMutation();
  const [updateDegree] = useUpdateDegreeMutation();

  const addDegree = async ({ degreeName }: { degreeName: string }) => {
    try {
      const result = await createDegree({
        variables: {
          name: degreeName,
          schoolId,
        },
        refetchQueries: refetchQueries,
      });

      return result.data;
    } catch (error) {
      return {
        createDegree: {
          data: null,
          success: false,
        },
      } as unknown as CreateDegreeMutation;
    }
  };

  const handleDeleteDegree = async () => {
    await deleteDegree({
      variables: {
        id: modalState.degreeId,
      },
      refetchQueries: refetchQueries,
    });
    setModalState({ state: DegreesTabModalState.DELETE_CONFIRMATION, degreeId: "", degreeName: "" });
    setSearchQuery("");
  };

  const handleActionModalOpen = async (degreeId: string, degreeName: string, modalType: DegreesTabModalState) => {
    setModalState({ state: modalType, degreeId, degreeName });
  };

  const handleModalClose = () => {
    setModalState({ state: DegreesTabModalState.CLOSED, degreeId: "", degreeName: "" });
  };

  const handleChangeDegreeName = async (formData: { degreeName: string }) => {
    try {
      const result = await updateDegree({
        variables: {
          id: modalState.degreeId,
          degree: {
            name: formData.degreeName,
          },
        },
        refetchQueries,
      });
      return result.data;
    } catch (error) {
      return {
        updateDegree: {
          data: null,
          success: false,
        },
      } as unknown as UpdateDegreeMutation;
    }
  };

  useEffect(() => {
    setPage(1);
  }, [searchQuery, sortBy]);

  useEffect(() => {
    degreesRefetch();
  }, [searchQuery, page, pageSize, sortBy]);

  if (modalState.state === DegreesTabModalState.DEGREE_ROSTER) {
    return (
      <DegreeRosterTabContainer
        degree={degreesData?.getDegreesBySchoolByQuery?.data.degrees.find(
          (degree) => degree.id === modalState.degreeId
        )}
        onBack={() => setModalState((prev) => ({ ...prev, state: DegreesTabModalState.CLOSED }))}
      />
    );
  }

  if (modalState.state === DegreesTabModalState.DEGREE_COURSES) {
    const selectedDegree = degreesData?.getDegreesBySchoolByQuery?.data.degrees.find(
      (degree) => degree.id === modalState.degreeId
    );
    return (
      <DegreeCoursesTabContainer
        degree={{ id: selectedDegree.id, name: selectedDegree.name, coursesHasCourse: selectedDegree.coursesHasCourse }}
        onBack={() => setModalState((prev) => ({ ...prev, state: DegreesTabModalState.CLOSED }))}
      />
    );
  }

  return (
    <>
      <View style={styles.container}>
        <View style={styles.header}>
          <View style={styles.headerRow}>
            <CustomText text="Manage Degrees" size="l" weight="bold" style={styles.headerText} />
            <View style={styles.buttonGroup}>
              <NewCustomActionButton
                text="Add a Degree"
                onPress={() => setModalState({ state: DegreesTabModalState.ADD_DEGREE, degreeId: "", degreeName: "" })}
                leftIcon={<PlusCircle color={primaryColor} />}
              />
            </View>
          </View>
          <View style={styles.headerRow}>
            <View style={styles.subHeaderContainer}>
              <CustomText text="Total Degrees" size="m" weight="semiBold" style={styles.subHeaderText} />
              <CustomText
                text={`(${degreesData?.getDegreesBySchoolByQuery?.data.totalCount || 0})`}
                size="m"
                weight="semiBold"
                style={styles.subHeaderText}
                useTranslationText={false}
              />
            </View>
            <View style={styles.controlsContainer}>
              <SortingDropdown
                sortBy={sortBy}
                setSortBy={setSortBy}
                sortDropdownOpen={sortDropdownOpen}
                setSortDropdownOpen={setSortDropdownOpen}
                sortItems={SORT_ITEMS}
                label="Sort by"
              />
              <CustomSearchInput searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
            </View>
          </View>
        </View>
        <DegreesTable
          degrees={degreesData?.getDegreesBySchoolByQuery?.data.degrees || []}
          onActionModalOpen={handleActionModalOpen}
        />
        <TableControlsContainer
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          totalCount={totalItemsCount}
        />
      </View>
      <NewCustomAxioActionModal
        visible={modalState.state === DegreesTabModalState.DELETE_DEGREE}
        onClose={handleModalClose}
        onAction={handleDeleteDegree}
        modalTexts={modals.deleteDegree}
        contextualText={modalState.degreeName}
      />
      <NewCustomAxioActionModal
        visible={modalState.state === DegreesTabModalState.DELETE_CONFIRMATION}
        onClose={handleModalClose}
        modalTexts={modals.deleteConfirmation}
        contextualText={modalState.degreeName}
      />
      <AddDegreeModal
        visible={modalState.state === DegreesTabModalState.ADD_DEGREE}
        onClose={handleModalClose}
        onSubmit={addDegree}
      />
      <UpdateDegreeModal
        visible={modalState.state === DegreesTabModalState.CHANGE_NAME}
        onClose={handleModalClose}
        onSubmit={handleChangeDegreeName}
        currentDegreeName={modalState.degreeName}
      />
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: 24,
    backgroundColor: "#fff",
    borderRadius: 8,
    flex: 1,
  },
  header: {
    flexDirection: "column",
    width: "100%",
    marginBottom: 24,
    zIndex: 3000,
    elevation: 3000,
  },
  headerRow: {
    flexDirection: "row",
    width: "100%",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 16,
    zIndex: 3000,
    elevation: 3000,
  },
  buttonGroup: {
    flexDirection: "row",
    gap: 12,
  },
  headerText: {
    color: "#000",
  },
  subHeaderContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  subHeaderText: {
    color: "#000",
  },
  controlsContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
    zIndex: 3000,
    elevation: 3000,
  },
});

export default DegreesTabContainer;
